import {
    Decimal,
    ICachingList,
    SubscribeState,
    TimeGet,
    TimeRelationItem,
    TimeRelationType,
    Ulid,
} from "@kase/my-marathon-communication";
import { signal } from "@preact/signals";
import { intervalToDuration } from "date-fns";
import { ApiSliceParams } from "../interfaces";
import { createEmitter, createListController } from "../utils";
import {
    TimeReport,
    TimeReportFilter,
    UpdateReportRequest,
} from "./interfaces";
import { getReportStart } from "./utils";

const getKey = (filter: TimeReportFilter) => {
    if ("project" in filter) {
        return `p-${filter.project.toString()}`;
    }

    return `${filter.year}-${filter.month}`;
};

export const makeReport = ({ connection, company }: ApiSliceParams) => {
    const { emit, on, off } = createEmitter<TimeReport>();

    const bindRelations = (
        reports: TimeReport[],
        relations: readonly Readonly<TimeRelationItem>[],
    ) => {
        for (const item of relations) {
            const report = reports.find(
                (r) => r.id === item.time_report.toString(),
            );

            if (!report) {
                continue;
            }

            if (item.kind === TimeRelationType.Track) {
                report.relation.value = {
                    company: item.company,
                    kind: item.kind,
                    relation_id: item.id,
                    time_report: item.time_report,
                    track: {
                        id: item.track,
                        track: item.track,
                    },
                };
            } else if (item.kind === TimeRelationType.Calendar) {
                report.relation.value = {
                    company: item.company,
                    kind: item.kind,
                    relation_id: item.id,
                    time_report: item.time_report,
                    calendar: {
                        id: item.id,
                        calendar_id: item.calendar_id,
                        event_id: item.event_id,
                        service: item.service,
                        etag: item.etag,
                    },
                };
            } else if (item.kind === TimeRelationType.Time) {
                report.relation.value = {
                    company: item.company,
                    kind: item.kind,
                    relation_id: item.id,
                    time_report: item.time_report,
                    timeParent: {
                        id: item.id,
                        parent: item.parent,
                    },
                };
            }
        }
    };

    const loadInitialRelations = async (
        list: ICachingList<TimeRelationItem>,
        reports: TimeReport[],
    ) => {
        for await (const { kind, values } of list) {
            if (kind === SubscribeState.Add || kind === SubscribeState.Update) {
                bindRelations(reports, values);
            } else if (
                kind === SubscribeState.LoadedCache ||
                kind === SubscribeState.Loaded
            ) {
                break;
            }
        }
    };

    const loadFinalRelations = async (
        list: ICachingList<TimeRelationItem>,
        reports: TimeReport[],
    ) => {
        for await (const { kind, values } of list) {
            if (kind === SubscribeState.Add || kind === SubscribeState.Update) {
                bindRelations(reports, values);
            } else if (kind === SubscribeState.Loaded) {
                break;
            }
        }
    };

    const {
        items: reports,
        loading,
        loaded,
        listen,
        whenLoaded,
    } = createListController<TimeGet, TimeReport, TimeReportFilter>({
        name: "report",
        connection,
        list: (con, filter) =>
            con.time.items({
                company: company.value,
                year: "year" in filter ? filter.year : undefined,
                month: "month" in filter ? filter.month : undefined,
                project: "project" in filter ? filter.project : undefined,
            }),
        emit,
        processBatch: async (items) => {
            const con = connection.value;
            const finalReports: TimeReport[] = items.map((r) => ({
                ...r,
                _id: r.id,
                id: r.id.toString(),
                relation: signal(null),
            }));
            const list = con.time.relation.items({
                company: company.value,
                time_report: items.map((i) => i.id),
            });

            await loadInitialRelations(list, finalReports);

            loadFinalRelations(list, finalReports);

            return finalReports;
        },
        getKey,
    });

    const remove = async (id: Readonly<Ulid>) => {
        const con = connection.value;

        return con.time.delete({ id, company: company.value });
    };

    const update = async ({
        id,
        start,
        end,
        project,
        feeCode,
        feeCategory,
        comment,
        onlyDuration,
    }: UpdateReportRequest) => {
        const con = connection.value;
        const lob =
            feeCategory && Ulid.ZERO.equals(feeCategory)
                ? undefined
                : feeCategory;
        const duration = intervalToDuration({ start, end });
        const hours =
            (duration.days ?? 0) * 24 +
            (duration.hours ?? 0) +
            (duration.minutes ?? 0) / 60 +
            (duration.seconds ?? 0) / 3600;

        // @TODO: move patch logic here (send only changed fields)
        await con.time.update({
            company: company.value,
            id,
            project,
            description: comment,
            start: getReportStart(start, onlyDuration),
            line_type: {
                $case: "fee",
                fee: {
                    hours: new Decimal(hours),
                    feecode: feeCode,
                    lob,
                },
            },
        });
    };

    const getByTrack = (id: Readonly<Ulid>) => {
        return reports.value.find((r) =>
            r.relation.value?.relation_id.equals(id),
        );
    };

    const getByCalendar = (
        service: string,
        calendarId: string,
        eventId: string,
    ) => {
        return reports.value.find(
            (r) =>
                // @TODO: temporary disabled
                // it is disabled because of wrong "service" for microsoft events
                // r.relation.value?.calendar?.service === service &&
                // r.relation.value?.calendar?.calendar_id === calendarId &&
                r.relation.value?.calendar?.event_id === eventId,
        );
    };

    const getById = (id: Readonly<Ulid> | string) => {
        const finalId = typeof id === "string" ? id : id.toString();

        return reports.value.find((r) => r.id === finalId);
    };

    return {
        reports,
        listen,
        loading,
        loaded,
        getByTrack,
        getByCalendar,
        getById,
        remove,
        update,
        whenLoaded,
        on,
        off,
    };
};
